import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import type { VendorPerformanceResponse, VendorPerformancePeriod } from '@signalvote/shared';
import { Skeleton } from '../ui/skeleton';
import { CollapsibleSection } from './CollapsibleSection';

interface VendorPerformanceTrendChartProps {
  data: VendorPerformanceResponse | undefined;
  isLoading: boolean;
}

function CustomTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ payload: VendorPerformancePeriod }>;
  label?: string;
}) {
  if (!active || !payload?.length || !label) return null;
  const period = payload[0].payload;
  return (
    <div className="rounded border bg-background p-2 text-xs shadow-sm">
      <p className="font-medium">{period.label}</p>
      <p>Doors: {period.doorsKnocked}</p>
      <p>Hours: {period.hoursWorked.toFixed(1)}</p>
      <p>Doors/hr: {period.doorsPerHour.toFixed(1)}</p>
      <p>Canvassers: {period.canvasserCount}</p>
    </div>
  );
}

export function VendorPerformanceTrendChart({ data, isLoading }: VendorPerformanceTrendChartProps) {
  if (isLoading) {
    return (
      <CollapsibleSection sectionId="vendor-performance-trend" title="Performance Trend">
        <Skeleton className="h-[300px] w-full" />
      </CollapsibleSection>
    );
  }

  const periods = data?.periods ?? [];

  return (
    <CollapsibleSection sectionId="vendor-performance-trend" title="Performance Trend">
      {periods.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No completed shift data for this vendor
        </p>
      ) : (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={periods}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12 }}
              />
              <YAxis label={{ value: 'Doors/hr', angle: -90, position: 'insideLeft' }} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="doorsPerHour"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4, fill: '#3b82f6' }}
              />
              {data?.benchmarkDoorsPerHour != null && (
                <ReferenceLine
                  y={data.benchmarkDoorsPerHour}
                  stroke="#9ca3af"
                  strokeDasharray="6 4"
                  label={`Campaign avg: ${data.benchmarkDoorsPerHour.toFixed(1)}`}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </CollapsibleSection>
  );
}
